import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Sparkline } from './Sparkline'

export interface StatTileProps {
  label: string
  value: string
  /** Small secondary line under the value, e.g. a derived rate. */
  sub?: string | null
  icon: LucideIcon
  /** Accent colour — pass an `--ad-color-*` token, e.g. `var(--ad-color-1)`. */
  color?: string
  /** Optional trend series rendered full-bleed along the bottom edge. */
  trend?: number[]
  /** Makes the tile a toggle button. */
  onClick?: () => void
  /** Highlighted state — only meaningful together with `onClick`. */
  selected?: boolean
  /**
   * A small control in the top-right corner, shown on hover or focus. It sits
   * beside the tile rather than inside it, as a button cannot hold a button.
   */
  action?: ReactNode
  className?: string
}

/**
 * Compact KPI tile: tinted icon chip, label and value, then a full-bleed
 * sparkline and a footer strip carrying the sub-line. Colour comes from a single accent token.
 * With `onClick` it renders as a toggle button and can show a selected state.
 */
export function StatTile({
  label,
  value,
  sub,
  icon: Icon,
  color = 'var(--ad-color-1)',
  trend,
  onClick,
  selected = false,
  action,
  className,
}: StatTileProps) {
  const hasTrend = !!trend && trend.length > 1 && trend.some((v) => v > 0)
  const interactive = !!onClick
  const Root = interactive ? 'button' : 'div'

  const tile = (
    <Root
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      aria-pressed={interactive ? selected : undefined}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-lg border bg-(--ad-surface-raised) text-left transition-all',
        interactive &&
          'cursor-pointer hover:shadow-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        interactive && !selected && 'opacity-80 hover:opacity-100',
        className
      )}
      style={{
        borderColor: selected
          ? color
          : `color-mix(in oklab, ${color} 25%, var(--border))`,
        background: selected
          ? `color-mix(in oklab, ${color} 8%, var(--card))`
          : undefined,
        boxShadow: selected ? `inset 0 0 0 1px ${color}` : undefined,
      }}
    >
      <div className="flex flex-col gap-1 px-2.5 pt-2 pb-2">
        <div className="flex items-center gap-1.5">
          <span
            className="flex size-5 shrink-0 items-center justify-center rounded-md"
            style={{
              color: selected ? 'var(--card)' : color,
              background: selected ? color : `color-mix(in oklab, ${color} 16%, transparent)`,
            }}
          >
            <Icon className="size-3" />
          </span>
          <span
            className="truncate text-[11px] font-medium"
            style={{ color: selected ? color : 'var(--ad-text-muted)' }}
          >
            {label}
          </span>
        </div>

        <div className="text-lg leading-none font-semibold tabular-nums">{value}</div>
      </div>

      {hasTrend && (
        <div className="pointer-events-none mt-auto">
          <Sparkline data={trend} color={color} height={18} strokeWidth={1.5} />
        </div>
      )}

      {/* Always rendered, so tiles without a sub-line keep the same height. */}
      <div className="mt-auto truncate px-2.5 py-1 text-[11px] text-muted-foreground">
        {sub || '\u00a0'}
      </div>
    </Root>
  )

  if (!action) return tile
  return (
    <div className="group/tile relative flex [&>*:first-child]:flex-1">
      {tile}
      <div className="absolute top-1.5 right-1.5 opacity-0 transition-opacity group-hover/tile:opacity-100 focus-within:opacity-100">
        {action}
      </div>
    </div>
  )
}
